import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText,
  Download,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Building2,
  Wallet,
  FileSpreadsheet,
  ChevronRight,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Table, Column } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { ConfirmDeleteModal } from '../components/ui/ConfirmDeleteModal';
import {
  api,
  getPropertyDocumentDownloadUrl,
  getPropertyCoverImageUrl,
  PROPERTY_IMAGE_PLACEHOLDER,
} from '../services/api';
import { DocumentActions } from '../components/documents/DocumentActions';
import { Investment, AccountStatement, InvestorDocument } from '../types';
import {
  formatDate,
  formatDateSpanishLong,
  formatDateTime,
  formatFechaCorta,
  parseInputToDate,
} from '../utils/date';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/** Días antes del vencimiento para considerar "por vencer" */
const DOC_EXPIRY_WARNING_DAYS = 30;

type DocStatus = 'vigente' | 'por_vencer' | 'vencido' | 'sin_fecha';

const CHART_COLORS = ['#2563eb', '#16a34a', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#4b5563'];

function getDocumentStatus(expiresAt: string | undefined): DocStatus {
  if (!expiresAt) return 'sin_fecha';
  const exp = new Date(expiresAt);
  exp.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = Math.ceil((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return 'vencido';
  if (daysLeft <= DOC_EXPIRY_WARNING_DAYS) return 'por_vencer';
  return 'vigente';
}

function DocumentStatusBadge({ status }: { status: DocStatus }) {
  const config = {
    vigente: { label: 'Vigente', className: 'bg-green-100 text-green-800', Icon: CheckCircle },
    por_vencer: { label: 'Por vencer', className: 'bg-amber-100 text-amber-800', Icon: Clock },
    vencido: { label: 'Vencido', className: 'bg-red-100 text-red-800', Icon: AlertTriangle },
    sin_fecha: { label: 'Sin fecha', className: 'bg-gray-100 text-gray-600', Icon: FileText },
  };
  const { label, className, Icon } = config[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}

/** Detecta si el error indica que no existe perfil de inversionista (404). */
function isNoInvestorProfileError(message: string | undefined): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return lower.includes('perfil de inversionista') || lower.includes('no se encontró');
}

export const MyAccount: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [profileMissing, setProfileMissing] = useState(false);
  const [completingProfile, setCompletingProfile] = useState(false);
  const [completeProfileForm, setCompleteProfileForm] = useState({ name: '', phone: '' });
  const [activeTab, setActiveTab] = useState<
    'general' | 'properties' | 'investments' | 'documents' | 'statements' | 'history'
  >('general');
  const tabsRef = useRef<HTMLDivElement>(null);

  const switchTab = (tab: typeof activeTab) => {
    setActiveTab(tab);
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };
  const [isStatementDetailOpen, setIsStatementDetailOpen] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState<AccountStatement | null>(null);
  const [changeHistory] = useState<any[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailMutationLoading, setEmailMutationLoading] = useState(false);
  const [emailMutationError, setEmailMutationError] = useState<string | null>(null);
  const [newDocument, setNewDocument] = useState({
    title: '',
    fileUrl: '',
    fileType: '',
    documentType: '',
    expiresAt: '',
  });
  const [mutationError, setMutationError] = useState<string | null>(null);

  const { data: investor, isLoading: loading, error: queryError, refetch: refetchProfile } = useQuery({
    queryKey: ['my-investor-profile'],
    queryFn: () => api.getMyInvestorProfile(),
    staleTime: 60_000,
    retry: (_, error: any) => !isNoInvestorProfileError(error?.message),
  });

  const { data: roiData } = useQuery({
    queryKey: ['my-investor-roi'],
    queryFn: () => api.getMyInvestorROI() as Promise<any>,
    enabled: !!investor?.id,
    staleTime: 60_000,
  });

  const { data: myStatementsRaw } = useQuery({
    queryKey: ['my-account-statements'],
    queryFn: () => api.getMyAccountStatements(),
    enabled: !!investor?.id,
    staleTime: 60_000,
  });

  const accountStatements = useMemo(() => {
    const rows = Array.isArray(myStatementsRaw) ? myStatementsRaw : [];
    return rows.map((r: any) => ({
      ...r,
      propertyName: r.property?.name ?? r.propertyName ?? '—',
      income: Number(r.income ?? 0),
      expenses: Number(r.expenses ?? 0),
      netIncome: Number(r.netIncome ?? 0),
      investorShare: Number(r.investorShare ?? 0),
      ownershipPct: Number(r.ownershipPct ?? 0),
    })) as AccountStatement[];
  }, [myStatementsRaw]);
  const queryErrorMessage = useMemo(() => {
    const msg = queryError ? (queryError as Error)?.message : null;
    if (msg && isNoInvestorProfileError(msg)) return null;
    return msg ?? null;
  }, [queryError]);
  const error = mutationError || queryErrorMessage;
  React.useEffect(() => {
    setProfileMissing(!!(queryError && isNoInvestorProfileError((queryError as Error)?.message)));
  }, [queryError]);

  // Inversiones del perfil API (con propertyName para la tabla)
  const investments = useMemo(() => {
    if (!investor?.investments) return [];
    return investor.investments.map((inv: any) => ({
      ...inv,
      propertyName: inv.properties?.name || inv.propertyName || '—',
    }));
  }, [investor]);

  /** Propiedades con inversión activa o completada (sin canceladas), una fila por activo. */
  const myInvestedProperties = useMemo(() => {
    type Row = {
      id: string;
      name: string;
      city?: string;
      state?: string | null;
      coverImage?: string | null;
      totalOwnershipPct: number;
      hasActive: boolean;
    };
    const map = new Map<string, Row>();
    for (const inv of investments as any[]) {
      if (inv.status === 'cancelled') continue;
      const p = inv.properties;
      if (!p?.id) continue;
      const pct = Number(inv.ownershipPct ?? 0) || 0;
      const cur = map.get(p.id);
      if (cur) {
        cur.totalOwnershipPct += pct;
        if (inv.status === 'active') cur.hasActive = true;
      } else {
        map.set(p.id, {
          id: p.id,
          name: p.name || '—',
          city: p.city,
          state: p.state,
          coverImage: p.coverImage,
          totalOwnershipPct: pct,
          hasActive: inv.status === 'active',
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }),
    );
  }, [investments]);

  const investorDocuments = useMemo(() => {
    const docs = investor?.investor_documents ?? investor?.documents;
    return Array.isArray(docs) ? docs : [];
  }, [investor]);

  // Resumen de estados de cuenta (hook debe ir antes de cualquier return)
  const summaryData = useMemo(() => {
    if (!investor) return { totalAccumulatedIncome: 0, totalNetFlow: 0, totalGain: 0, averageAnnualROI: 0 };
    const num = (v: unknown) => {
      const n = typeof v === 'number' ? v : Number(v ?? 0);
      return Number.isFinite(n) ? n : 0;
    };
    const investorStatements = accountStatements.filter((stmt) => stmt.investorId === investor.id);
    const statementsByProperty = investorStatements.reduce((acc, stmt) => {
      const key = stmt.propertyId;
      if (!acc[key]) acc[key] = { propertyId: stmt.propertyId, propertyName: stmt.propertyName || '', statements: [] };
      acc[key].statements.push(stmt);
      return acc;
    }, {} as Record<string, { propertyId: string; propertyName: string; statements: AccountStatement[] }>);
    const propertyGroups = Object.values(statementsByProperty);
    const latestStatements = propertyGroups.map((group) => {
      const sorted = [...group.statements].sort((a, b) => (a.year !== b.year ? b.year - a.year : b.month - a.month));
      return sorted[0];
    }).filter(Boolean);
    const avgAnnual =
      latestStatements.length > 0
        ? latestStatements.reduce((sum, stmt) => sum + num((stmt as any).annualROI), 0) /
          latestStatements.length
        : 0;
    return {
      totalAccumulatedIncome: latestStatements.reduce((sum, stmt) => sum + num((stmt as any).accumulatedYearlyIncome), 0),
      totalNetFlow: latestStatements.reduce((sum, stmt) => sum + num((stmt as any).totalNetFlow), 0),
      totalGain: latestStatements.reduce((sum, stmt) => sum + num((stmt as any).totalGain), 0),
      averageAnnualROI: Number.isFinite(avgAnnual) ? avgAnnual : 0,
    };
  }, [investor, accountStatements]);

  const monthlyStackedRows = useMemo(() => {
    const rows = roiData?.monthlyStacked ?? [];
    if (!Array.isArray(rows) || rows.length === 0) {
      return { chartData: [], stackIds: [], idToName: new Map<string, string>() };
    }
    const stackIds = Array.from(
      new Set(
        rows.flatMap((r: any) => (r?.stacks ?? []).map((s: any) => String(s.propertyId))),
      ),
    );
    const idToName = new Map<string, string>();
    rows.forEach((r: any) =>
      (r?.stacks ?? []).forEach((s: any) =>
        idToName.set(String(s.propertyId), String(s.propertyName ?? '—')),
      ),
    );
    const chartData = rows.map((r: any) => {
      const row: Record<string, number | string> = { label: String(r.label ?? r.key ?? '') };
      stackIds.forEach((id) => {
        const stack = (r?.stacks ?? []).find((s: any) => String(s.propertyId) === id);
        row[id] = stack ? Number(stack.investorShare ?? 0) : 0;
      });
      return row;
    });
    return { chartData, stackIds, idToName };
  }, [roiData]);

  const byPropertyPie = useMemo(() => {
    const rows = roiData?.byProperty ?? [];
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows
      .map((r: any) => ({
        name: String(r.propertyName ?? '—'),
        value: Number(r.totalPaid ?? 0),
      }))
      .filter((x: any) => Number.isFinite(x.value) && x.value > 0);
  }, [roiData]);

  const handleDocumentFileChange = (fileUrl: string, fileType?: string) => {
    setNewDocument(prev => ({
      ...prev,
      fileUrl,
      fileType: fileType || 'pdf',
    }));
  };

  const handleAddDocument = async () => {
    if (!newDocument.title.trim() || !newDocument.fileUrl || !investor) return;
    try {
      await api.addMyInvestorDocument({
        title: newDocument.title.trim(),
        fileUrl: newDocument.fileUrl,
        fileType: newDocument.fileType,
        documentType: newDocument.documentType || undefined,
        expiresAt: newDocument.expiresAt || undefined,
      });
      queryClient.invalidateQueries({ queryKey: ['my-investor-profile'] });
      setNewDocument({ title: '', fileUrl: '', fileType: '', documentType: '', expiresAt: '' });
    } catch (e: any) {
      setMutationError(e.message || 'Error al agregar documento');
    }
  };

  const [pendingDeleteDocument, setPendingDeleteDocument] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [documentDeleteLoading, setDocumentDeleteLoading] = useState(false);

  const handleRemoveDocument = async () => {
    if (!investor || !pendingDeleteDocument) return;
    setDocumentDeleteLoading(true);
    setMutationError(null);
    try {
      await api.removeMyInvestorDocument(pendingDeleteDocument.id);
      queryClient.invalidateQueries({ queryKey: ['my-investor-profile'] });
      setPendingDeleteDocument(null);
    } catch (e: any) {
      setMutationError(e.message || 'Error al eliminar documento');
    } finally {
      setDocumentDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (profileMissing && !investor) {
    const handleCompleteProfile = async (e: React.FormEvent) => {
      e.preventDefault();
      const name = completeProfileForm.name.trim() || (user?.name ?? '').trim();
      const phone = completeProfileForm.phone.trim();
      if (!name) {
        setMutationError('Indica tu nombre para completar el perfil.');
        return;
      }
      setCompletingProfile(true);
      setMutationError(null);
      try {
        await api.completeMyInvestorProfile({ name, phone: phone || undefined });
        queryClient.invalidateQueries({ queryKey: ['my-investor-profile'] });
        setProfileMissing(false);
        setCompleteProfileForm({ name: '', phone: '' });
      } catch (err: any) {
        setMutationError(err.message || 'No se pudo completar el perfil. Intenta de nuevo.');
      } finally {
        setCompletingProfile(false);
      }
    };

    return (
      <div className="max-w-md mx-auto">
        <Card className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Completa tu perfil de inversionista</h2>
          <p className="text-sm text-gray-600 mb-6">
            Tu cuenta está activa pero falta el perfil de inversionista. Completa los datos para acceder a Mi Cuenta.
          </p>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <form onSubmit={handleCompleteProfile} className="space-y-4">
            <div>
              <label htmlFor="complete-name" className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                id="complete-name"
                type="text"
                value={completeProfileForm.name || (user?.name ?? '')}
                onChange={(e) => setCompleteProfileForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Tu nombre completo"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent"
                required
              />
            </div>
            <div>
              <label htmlFor="complete-phone" className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono (opcional)
              </label>
              <input
                id="complete-phone"
                type="text"
                value={completeProfileForm.phone}
                onChange={(e) => setCompleteProfileForm((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+52 55 1234 5678"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#25326B] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correo</label>
              <p className="text-sm text-gray-600">{user?.email ?? '—'}</p>
            </div>
            <button
              type="submit"
              disabled={completingProfile}
              className="w-full bg-[#25326B] hover:bg-[#1a2550] text-white font-medium py-2.5 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {completingProfile ? 'Guardando...' : 'Completar perfil'}
            </button>
          </form>
        </Card>
      </div>
    );
  }

  if (error || !investor) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{error || 'No se encontró información de inversionista para tu cuenta'}</p>
      </div>
    );
  }

  const activeInvestments = investments.filter((inv: any) => inv.status === 'active');

  const historyInvestments = [...investments].sort(
    (a, b) => new Date((b as any).createdAt || 0).getTime() - new Date((a as any).createdAt || 0).getTime()
  );

  // Historial de cambios del inversionista
  const investorChangeHistory = changeHistory
    .filter((ch) => ch.investorId === investor.id)
    .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());

  // Estados de cuenta agrupados por propiedad
  const investorStatements = accountStatements.filter(
    (stmt) => stmt.investorId === investor.id
  );

  const statementsByProperty = investorStatements.reduce((acc, stmt) => {
    const key = stmt.propertyId;
    if (!acc[key]) {
      acc[key] = {
        propertyId: stmt.propertyId,
        propertyName: stmt.propertyName || '',
        statements: [],
      };
    }
    acc[key].statements.push(stmt);
    return acc;
  }, {} as Record<string, { propertyId: string; propertyName: string; statements: AccountStatement[] }>);

  // Ordenar estados de cuenta por año y mes dentro de cada propiedad
  Object.values(statementsByProperty).forEach((group) => {
    group.statements.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
  });

  const propertyGroups = Object.values(statementsByProperty);

  const investmentColumns: Column<Investment>[] = [
    { header: 'Propiedad', accessor: 'propertyName' },
    {
      header: 'Monto',
      accessor: (row) => formatCurrency((row as any).amount),
    },
    {
      header: 'Participación',
      accessor: (row) => `${Number((row as any).ownershipPct || 0).toLocaleString('es-MX', { maximumFractionDigits: 4 })}%`,
    },
    {
      header: 'Fecha Inicio',
      accessor: (row) => formatDateLongTwoDigitDay((row as any).startDate),
    },
    {
      header: 'Estado',
      accessor: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : row.status === 'completed'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status === 'active' ? 'Activa' : row.status === 'completed' ? 'Completada' : 'Cancelada'}
        </span>
      ),
    },
  ];

  const formatCurrency = (value: unknown) => {
    const n = typeof value === 'number' ? value : Number(value ?? 0);
    return `$${(Number.isFinite(n) ? n : 0).toLocaleString('es-MX')}`;
  };

  const formatDateLongTwoDigitDay = (value: unknown) => {
    const d = parseInputToDate(value as any);
    if (!d) return '—';
    return d.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header / Hero */}
      <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 truncate">
              Mi Cuenta
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Hola, <span className="font-semibold text-gray-900">{investor.name}</span>. Aquí tienes tu resumen y tus documentos/estados de cuenta.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => switchTab('statements')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              <FileSpreadsheet className="w-4 h-4 text-primary" />
              Estados de cuenta
            </button>
            <button
              type="button"
              onClick={() => switchTab('documents')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              <FileText className="w-4 h-4 text-primary" />
              Documentos
            </button>
            <button
              type="button"
              onClick={() => switchTab('properties')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              <Building2 className="w-4 h-4 text-primary" />
              Mis propiedades
            </button>
            <button
              type="button"
              onClick={() => switchTab('investments')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50"
            >
              <Building2 className="w-4 h-4 text-primary" />
              Inversiones
            </button>
          </div>
        </div>

        {/* KPI row */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500">Total invertido</p>
              <Wallet className="w-4 h-4 text-primary" />
            </div>
            <p className="mt-1 text-xl font-bold text-gray-900">
              {formatCurrency(investor.totalInvested)}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500">Propiedades</p>
              <Building2 className="w-4 h-4 text-primary" />
            </div>
            <p className="mt-1 text-xl font-bold text-gray-900">{investor.propertiesCount}</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500">Flujo neto total</p>
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <p className="mt-1 text-xl font-bold text-gray-900">
              {formatCurrency(summaryData.totalNetFlow)}
            </p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-500">ROI promedio anual</p>
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <p className="mt-1 text-xl font-bold text-gray-900">
              {Number(summaryData.averageAnnualROI || 0).toFixed(2)}%
            </p>
          </div>
        </div>

        {/* Charts */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Participación mensual</p>
                <p className="text-xs text-gray-500">Últimos 12 meses (por propiedad)</p>
              </div>
            </div>
            {monthlyStackedRows.chartData.length === 0 || monthlyStackedRows.stackIds.length === 0 ? (
              <p className="text-sm text-gray-500 mt-3">Aún no hay datos suficientes para la gráfica.</p>
            ) : (
              <div className="h-64 w-full mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyStackedRows.chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => formatCurrency(v)} width={84} />
                    <Tooltip
                      formatter={(value: number, name: string) => [
                        formatCurrency(value),
                        monthlyStackedRows.idToName.get(name) ?? name,
                      ]}
                    />
                    <Legend formatter={(value) => monthlyStackedRows.idToName.get(String(value)) ?? String(value)} />
                    {monthlyStackedRows.stackIds.map((pid: string, i: number) => (
                      <Bar
                        key={pid}
                        dataKey={pid}
                        stackId="a"
                        fill={CHART_COLORS[i % CHART_COLORS.length]}
                        name={pid}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Distribución por propiedad</p>
              <p className="text-xs text-gray-500">Total recibido (participación)</p>
            </div>
            {byPropertyPie.length === 0 ? (
              <p className="text-sm text-gray-500 mt-3">Aún no hay datos suficientes para la gráfica.</p>
            ) : (
              <div className="h-64 w-full mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={byPropertyPie}
                      cx="50%"
                      cy="50%"
                      outerRadius={86}
                      dataKey="value"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {byPropertyPie.map((_: any, idx: number) => (
                        <Cell key={`cell-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: number) => formatCurrency(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div ref={tabsRef} className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-2">
          {[
            { id: 'general', label: 'Información General' },
            { id: 'properties', label: 'Mis propiedades' },
            { id: 'documents', label: 'Documentos' },
            { id: 'statements', label: 'Estado de Cuenta' },
            { id: 'investments', label: 'Inversiones Activas' },
            { id: 'history', label: 'Historial' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-accent text-white'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'properties' && (
        <div>
          <h2 className="text-lg font-semibold mb-2">Propiedades donde inviertes</h2>
          <p className="text-sm text-gray-500 mb-4">
            Entra a cada activo para ver los documentos compartidos por la administración.
          </p>
          {myInvestedProperties.length === 0 ? (
            <Card>
              <p className="text-sm text-gray-600 py-6 text-center">
                Cuando tengas inversiones registradas, aparecerán aquí con acceso a sus documentos.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {myInvestedProperties.map((p) => (
                <Link
                  key={p.id}
                  to={`/mi-cuenta/propiedad/${p.id}`}
                  className="group rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-primary/40 hover:shadow-md transition-all"
                >
                  <div className="h-36 bg-gray-100 relative">
                    <img
                      src={
                        p.coverImage
                          ? getPropertyCoverImageUrl(p.coverImage)
                          : PROPERTY_IMAGE_PLACEHOLDER
                      }
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary">
                          {p.name}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {[p.city, p.state].filter(Boolean).join(' · ') || '—'}
                        </p>
                        {p.totalOwnershipPct > 0 && (
                          <p className="text-xs text-gray-600 mt-2">
                            Participación:{' '}
                            <span className="font-medium">
                              {p.totalOwnershipPct.toLocaleString('es-MX', {
                                maximumFractionDigits: 4,
                              })}
                              %
                            </span>
                            {p.hasActive ? (
                              <span className="ml-2 text-green-700">· Inversión activa</span>
                            ) : null}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 group-hover:text-primary" />
                    </div>
                    <span className="mt-3 inline-flex items-center text-sm font-medium text-primary">
                      Ver documentos
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'general' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card title="Información de Contacto">
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Nombre</label>
                <p className="text-base font-medium">{investor.name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600 block mb-1">Correos electrónicos</label>
                <div className="space-y-1.5">
                  {/* Correo principal */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{investor.email}</span>
                    <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">Principal</span>
                  </div>
                  {/* Correos adicionales */}
                  {(investor.additionalEmails ?? []).map((email: string) => (
                    <div key={email} className="flex items-center gap-2">
                      <span className="text-sm text-gray-800">{email}</span>
                      <button
                        type="button"
                        onClick={async () => {
                          setEmailMutationError(null);
                          setEmailMutationLoading(true);
                          try {
                            await api.removeMyInvestorEmail(email);
                            queryClient.invalidateQueries({ queryKey: ['my-investor-profile'] });
                          } catch (e: any) {
                            setEmailMutationError(e.message || 'Error al eliminar correo');
                          } finally {
                            setEmailMutationLoading(false);
                          }
                        }}
                        disabled={emailMutationLoading}
                        className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-40"
                        title="Eliminar correo"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Agregar correo */}
                <div className="mt-3 flex gap-2">
                  <input
                    type="email"
                    value={newEmailInput}
                    onChange={(e) => { setNewEmailInput(e.target.value); setEmailMutationError(null); }}
                    placeholder="Agregar correo adicional"
                    className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.currentTarget.form?.requestSubmit?.();
                    }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!newEmailInput.trim()) return;
                      setEmailMutationError(null);
                      setEmailMutationLoading(true);
                      try {
                        await api.addMyInvestorEmail(newEmailInput.trim());
                        queryClient.invalidateQueries({ queryKey: ['my-investor-profile'] });
                        setNewEmailInput('');
                      } catch (e: any) {
                        setEmailMutationError(e.message || 'Error al agregar correo');
                      } finally {
                        setEmailMutationLoading(false);
                      }
                    }}
                    disabled={emailMutationLoading || !newEmailInput.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agregar
                  </button>
                </div>
                {emailMutationError && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {emailMutationError}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-600">Teléfono</label>
                <p className="text-base font-medium">{investor.phone}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Estado</label>
                <p className="text-base font-medium">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      investor.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {investor.status === 'active' ? 'Activo' : 'Inactivo'}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Fecha de Registro</label>
                <p className="text-base font-medium">
                  {formatDateSpanishLong(investor.createdAt)}
                </p>
              </div>
            </div>
          </Card>

          <Card title="Resumen de Inversiones">
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Total Invertido</label>
                <p className="text-2xl font-bold text-primary">
                  ${investor.totalInvested.toLocaleString()}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Propiedades</label>
                <p className="text-2xl font-bold text-primary">{investor.propertiesCount}</p>
              </div>
              {summaryData.totalAccumulatedIncome > 0 && (
                <>
                  <div className="pt-4 border-t border-gray-200">
                    <label className="text-sm text-gray-600">Ingreso Acumulado del Año</label>
                    <p className="text-xl font-bold text-green-600">
                      {formatCurrency(summaryData.totalAccumulatedIncome)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Flujo Neto Total Recibido</label>
                    <p className="text-xl font-bold text-primary">
                      {formatCurrency(summaryData.totalNetFlow)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Ganancia Total</label>
                    <p className="text-xl font-bold text-primary">
                      {formatCurrency(summaryData.totalGain)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">ROI Promedio Anual</label>
                    <p className="text-xl font-bold text-primary">
                      {summaryData.averageAnnualROI.toFixed(2)}%
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'investments' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Inversiones Activas</h2>
          <Card>
            <Table data={activeInvestments} columns={investmentColumns} />
          </Card>
        </div>
      )}

      {activeTab === 'documents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold">Documentos</h2>
              <p className="text-sm text-gray-500 mt-1">
                Documentos adjuntos a tu cuenta. Revisa el estado: vigente, por vencer o vencido.
              </p>
            </div>
          </div>
          <Card>
            {investorDocuments.length > 0 ? (
              <>
                {(() => {
                  const counts = { vigente: 0, por_vencer: 0, vencido: 0, sin_fecha: 0 };
                  investorDocuments.forEach((doc) => {
                    const st = getDocumentStatus((doc as any).expiresAt);
                    if (st in counts) counts[st as keyof typeof counts]++;
                  });
                  const hasAnyStatus = counts.vencido > 0 || counts.por_vencer > 0 || counts.vigente > 0;
                  return hasAnyStatus ? (
                    <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                      {counts.vigente > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
                          <CheckCircle className="w-4 h-4" />
                          <strong>{counts.vigente}</strong> vigente{counts.vigente !== 1 ? 's' : ''}
                        </span>
                      )}
                      {counts.por_vencer > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-amber-700">
                          <Clock className="w-4 h-4" />
                          <strong>{counts.por_vencer}</strong> por vencer
                        </span>
                      )}
                      {counts.vencido > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm text-red-700">
                          <AlertTriangle className="w-4 h-4" />
                          <strong>{counts.vencido}</strong> vencido{counts.vencido !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  ) : null;
                })()}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {investorDocuments.map((document) => {
                    const docStatus = getDocumentStatus((document as any).expiresAt);
                    return (
                      <div
                        key={document.id}
                        className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                                <FileText className="w-6 h-6 text-primary" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-medium text-gray-900 truncate">
                                {document.title}
                              </h3>
                              {(document as any).documentType && (
                                <p className="text-xs text-gray-500 mt-0.5 capitalize">
                                  {(document as any).documentType.replace(/_/g, ' ')}
                                </p>
                              )}
                              <div className="mt-1.5">
                                <DocumentStatusBadge status={docStatus} />
                              </div>
                              {(document as any).expiresAt && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Vence: {formatFechaCorta((document as any).expiresAt)}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                Subido: {formatDate(document.uploadedAt)}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setPendingDeleteDocument({
                                id: document.id,
                                title: document.title,
                              })
                            }
                            className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Eliminar documento"
                            aria-label="Eliminar documento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {document.fileUrl ? (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <DocumentActions
                              title={document.title}
                              fileUrl={document.fileUrl}
                              downloadName={document.title || undefined}
                            />
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-12 mb-6">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No hay documentos adjuntos a tu cuenta</p>
              </div>
            )}

            {/* Formulario para agregar nuevo documento */}
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Agregar Nuevo Documento</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título del Documento
                  </label>
                  <input
                    type="text"
                    value={newDocument.title}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                    placeholder="Ej: Identificación Oficial"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de documento
                  </label>
                  <select
                    value={newDocument.documentType}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, documentType: e.target.value }))}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Seleccionar tipo (opcional)</option>
                    <option value="identificacion">Identificación</option>
                    <option value="comprobante_domicilio">Comprobante de domicilio</option>
                    <option value="fiel">FIEL / CSF</option>
                    <option value="caratula_bancaria">Carátula bancaria</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de vencimiento (opcional)
                  </label>
                  <input
                    type="date"
                    value={newDocument.expiresAt}
                    onChange={(e) => setNewDocument(prev => ({ ...prev, expiresAt: e.target.value }))}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si el documento tiene vigencia anual o fecha de caducidad, indícala aquí.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Archivo
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          handleDocumentFileChange(reader.result as string, file.type);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent/90"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddDocument}
                  disabled={!newDocument.title.trim() || !newDocument.fileUrl}
                  className="flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  <span>Agregar Documento</span>
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'statements' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Estados de Cuenta</h2>
            <p className="text-sm text-gray-500 mb-6">
              Estados de cuenta agrupados por propiedad y mes
            </p>
          </div>

          {propertyGroups.length > 0 ? (
            <div className="space-y-6">
              {propertyGroups.map((propertyGroup) => (
                <Card key={propertyGroup.propertyId} title={propertyGroup.propertyName}>
                  <div className="space-y-4">
                    {propertyGroup.statements.map((statement) => (
                      <div
                        key={statement.id}
                        onClick={() => {
                          setSelectedStatement(statement);
                          setIsStatementDetailOpen(true);
                        }}
                        className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all cursor-pointer"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-3">
                              <h3 className="text-base font-semibold text-gray-900">
                                {statement.period}
                              </h3>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                {statement.ownershipPct}% Participación
                              </span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <label className="text-xs text-gray-500">Ingresos</label>
                                <p className="text-sm font-medium text-green-600">
                                  ${statement.income.toLocaleString('es-MX')}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500">Gastos</label>
                                <p className="text-sm font-medium text-red-600">
                                  ${statement.expenses.toLocaleString('es-MX')}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500">Ingreso Neto</label>
                                <p className="text-sm font-medium text-primary">
                                  ${statement.netIncome.toLocaleString('es-MX')}
                                </p>
                              </div>
                              <div>
                                <label className="text-xs text-gray-500">Tu Participación</label>
                                <p className="text-sm font-medium text-primary">
                                  ${statement.investorShare.toLocaleString('es-MX')}
                                </p>
                              </div>
                            </div>
                          </div>
                          {statement.fileUrl && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  getPropertyDocumentDownloadUrl(statement.fileUrl),
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                              }}
                              className="ml-4 p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Descargar PDF"
                            >
                              <Download className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <div className="text-center py-12">
                <p className="text-gray-500">No hay estados de cuenta disponibles</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold mb-4">Historial de Inversiones</h2>
            <p className="text-sm text-gray-500 mb-6">
              Todas tus inversiones ordenadas por fecha (más recientes primero)
            </p>
          </div>
          <Card>
            <Table data={historyInvestments} columns={investmentColumns} />
          </Card>

          {investorChangeHistory.length > 0 && (
            <>
              <div className="mt-6">
                <h2 className="text-lg font-semibold mb-4">Historial de Cambios</h2>
              </div>
              <Card>
                <div className="space-y-4">
                  {investorChangeHistory.map((change) => (
                    <div
                      key={change.id}
                      className="p-4 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm text-gray-900">{change.description}</p>
                          {change.field && change.oldValue && change.newValue && (
                            <p className="text-xs text-gray-500 mt-1">
                              {change.field}: {change.oldValue} → {change.newValue}
                            </p>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatDateTime(change.changedAt)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Modal de Detalle de Estado de Cuenta */}
      {selectedStatement && (
        <Modal
          isOpen={isStatementDetailOpen}
          onClose={() => {
            setIsStatementDetailOpen(false);
            setSelectedStatement(null);
          }}
          title={`Estado de Cuenta - ${selectedStatement.period}`}
          size="xl"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Propiedad</label>
                <p className="text-base font-medium">{selectedStatement.propertyName}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Participación</label>
                <p className="text-base font-medium">{selectedStatement.ownershipPct}%</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Resumen Financiero</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600">Ingresos</label>
                  <p className="text-lg font-bold text-green-600">
                    ${selectedStatement.income.toLocaleString('es-MX')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Gastos</label>
                  <p className="text-lg font-bold text-red-600">
                    ${selectedStatement.expenses.toLocaleString('es-MX')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Ingreso Neto</label>
                  <p className="text-lg font-bold text-primary">
                    ${selectedStatement.netIncome.toLocaleString('es-MX')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600">Tu Participación</label>
                  <p className="text-lg font-bold text-primary">
                    ${selectedStatement.investorShare.toLocaleString('es-MX')}
                  </p>
                </div>
              </div>
            </div>

            {selectedStatement.fileUrl && (
              <div className="border-t pt-4">
                <button
                  onClick={() =>
                    window.open(
                      getPropertyDocumentDownloadUrl(selectedStatement.fileUrl),
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Descargar PDF Completo</span>
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      <ConfirmDeleteModal
        isOpen={pendingDeleteDocument !== null}
        onClose={() => !documentDeleteLoading && setPendingDeleteDocument(null)}
        onConfirm={handleRemoveDocument}
        title="Eliminar documento"
        message={
          pendingDeleteDocument ? (
            <>
              ¿Eliminar el documento <strong>{pendingDeleteDocument.title}</strong> de tu
              expediente?
            </>
          ) : null
        }
        isLoading={documentDeleteLoading}
      />
    </div>
  );
};
